import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Settings, Shield } from "lucide-react";

interface SystemFunction {
  id: string;
  name: string;
  display_name: string;
  description: string;
  category: string;
  sort_order: number;
}

interface RolePermission {
  id: string;
  system_function_id: string;
  team: string;
  role_level: "user" | "adviser" | "admin" | "superadmin";
  is_enabled: boolean;
}

const TEAMS = ["Operations", "Advisory", "Investment", "Admin"] as const;
const LEVELS = ["user", "adviser", "admin", "superadmin"] as const;
type Team = typeof TEAMS[number];
type Level = typeof LEVELS[number];

const LEVEL_LABELS: Record<Level, string> = {
  user: "User",
  adviser: "Adviser",
  admin: "Admin",
  superadmin: "Super Admin",
};

// permissions[functionId][team][level] = boolean
type PermissionGrid = Record<string, Record<string, Record<string, boolean>>>;

const PermissionsManager = () => {
  const [systemFunctions, setSystemFunctions] = useState<SystemFunction[]>([]);
  const [grid, setGrid] = useState<PermissionGrid>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [fnRes, permRes] = await Promise.all([
        supabase.from("system_functions").select("*").order("category").order("sort_order"),
        supabase.from("role_permissions").select("id, system_function_id, team, role_level, is_enabled"),
      ]);
      if (fnRes.error) throw fnRes.error;
      if (permRes.error) throw permRes.error;

      const functions = fnRes.data || [];
      const perms = (permRes.data || []) as RolePermission[];

      const g: PermissionGrid = {};
      functions.forEach((f) => {
        g[f.id] = {};
        TEAMS.forEach((t) => {
          g[f.id][t] = {};
          LEVELS.forEach((l) => {
            const p = perms.find(
              (x) => x.system_function_id === f.id && x.team === t && x.role_level === l
            );
            g[f.id][t][l] = p?.is_enabled ?? false;
          });
        });
      });
      setSystemFunctions(functions);
      setGrid(g);
    } catch (error) {
      console.error("Error fetching permissions data:", error);
      toast({ title: "Error", description: "Failed to load permissions data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const updatePermission = async (functionId: string, team: Team, level: Level, isEnabled: boolean) => {
    setGrid((prev) => ({
      ...prev,
      [functionId]: {
        ...prev[functionId],
        [team]: { ...prev[functionId][team], [level]: isEnabled },
      },
    }));
    try {
      const { data: existing } = await supabase
        .from("role_permissions")
        .select("id")
        .eq("system_function_id", functionId)
        .eq("team", team)
        .eq("role_level", level)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("role_permissions")
          .update({ is_enabled: isEnabled })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("role_permissions")
          .insert({ system_function_id: functionId, team, role_level: level, is_enabled: isEnabled });
        if (error) throw error;
      }
      toast({ title: "Saved", description: "Permission updated" });
    } catch (error) {
      console.error("Error updating permission:", error);
      setGrid((prev) => ({
        ...prev,
        [functionId]: {
          ...prev[functionId],
          [team]: { ...prev[functionId][team], [level]: !isEnabled },
        },
      }));
      toast({ title: "Error", description: "Failed to update permission", variant: "destructive" });
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case "content": return "bg-blue-100 text-blue-800";
      case "documents": return "bg-green-100 text-green-800";
      case "portfolios": return "bg-purple-100 text-purple-800";
      case "meetings": return "bg-yellow-100 text-yellow-800";
      case "admin": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const groupedFunctions = systemFunctions.reduce((groups, f) => {
    (groups[f.category] ||= []).push(f);
    return groups;
  }, {} as Record<string, SystemFunction[]>);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-32">Loading permissions...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          User Permissions Management
        </CardTitle>
        <CardDescription>
          For each function, toggle access per <strong>Team × Access Level</strong> combination. A user is granted the function only if their specific (Team, Access Level) cell is enabled. Super Admins always have access regardless of the matrix.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {Object.entries(groupedFunctions).map(([category, functions]) => (
            <div key={category}>
              <div className="flex items-center gap-2 mb-3">
                <Badge className={getCategoryBadgeColor(category)} variant="secondary">
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Badge>
              </div>

              <div className="space-y-6">
                {functions.map((func) => (
                  <div key={func.id} className="border rounded-lg p-4">
                    <div className="mb-3">
                      <div className="font-medium">{func.display_name}</div>
                      <div className="text-sm text-muted-foreground">{func.description}</div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-32">Team</TableHead>
                            {LEVELS.map((l) => (
                              <TableHead key={l} className="text-center">{LEVEL_LABELS[l]}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {TEAMS.map((team) => (
                            <TableRow key={team}>
                              <TableCell className="font-medium">{team}</TableCell>
                              {LEVELS.map((level) => (
                                <TableCell key={level} className="text-center">
                                  <Checkbox
                                    checked={grid[func.id]?.[team]?.[level] || false}
                                    onCheckedChange={(checked) =>
                                      updatePermission(func.id, team, level, checked as boolean)
                                    }
                                    className="mx-auto"
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 mt-0.5 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              <strong>Note:</strong> Permissions require BOTH the user's Team and Access Level to match an enabled cell. Super Admins bypass this matrix entirely.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PermissionsManager;
